var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { ofActionCompleted } from '@ngxs/store';
import { MessageEnum } from '../models/message.enum';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { NextChapter, PreviousChapter, SelectChapterById } from 'src/app/story/state/chapters/story.chapters.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { take } from 'rxjs/operators';
import { Observable } from 'rxjs';
export function getTimelineEvents() {
    return [
        {
            eventName: EventsEnum.TimelineChapterTransition,
            aliasEvents: [EventsEnum.TimelineChaptersActivated],
            mapFn: function (event) {
                return {
                    previousChapter: event.oldChapterIndex,
                    currentChapter: event.newChapterIndex,
                    time: event.time
                };
            }
        }
    ];
}
function formatTimelineInfo(store, animationService) {
    var timeframe = animationService.getTimeFrame();
    return {
        time: animationService.getTime(),
        startTime: timeframe.firstTime,
        endTime: timeframe.lastTime,
        chapters: store
            .selectSnapshot(StoryChaptersState.getStoryChapters)
            .map(function (chapter) { return chapter.id; }),
        playing: animationService.isPlaying()
    };
}
export function getTimelineProcedures(store, actions, animationService) {
    return [
        {
            // Support Timeline.query according to J&J's usage.
            // Only need to support the data specified in TimelineIO.TimelineQueryResponse
            procName: MessageEnum.TimelineQuery,
            actionFn: function () {
                var chapters = store
                    .selectSnapshot(StoryChaptersState.getStoryChapters)
                    .map(function (chapter) {
                    var _a = chapter.info, description = _a.description, displayName = _a.displayName;
                    var annotations = chapter.labels.map(function (label) {
                        return {
                            title: label.title,
                            description: label.description
                        };
                    });
                    return {
                        description: description,
                        displayName: displayName,
                        annotations: annotations
                    };
                });
                return { chapters: chapters };
            }
        },
        {
            procName: MessageEnum.TimelineInfo,
            actionFn: function () { return formatTimelineInfo(store, animationService); }
        },
        {
            procName: MessageEnum.TimelinePlay,
            actionFn: function (params) {
                // hoops to jump through:
                // The app is responsible for managing chapter state, 
                // so we have to dispatch a chapter change, and wait until its done before we can play
                // we also have to map from global time to chapter, in the case of chapterId being absent from params.
                var loop = params.loop || false;
                if (params.time === undefined && params.chapterId === undefined) {
                    var currentChapterId = store.selectSnapshot(StoryChaptersState.getSelectedChapterId);
                    animationService.play({ numChapters: 1, loop: loop, queue: false, startChapterId: currentChapterId });
                    return true;
                }
                // time should not be lower than zero
                var timeLowerBound = Math.max(0, params.time);
                // time should not be greater than upper timeline bound
                var timeUpperBound = animationService.getTimeFrame().lastTime;
                var offsetToPreventPlayBleedingToZeroethChapter = .001;
                var adjTime = Math.min(timeLowerBound, timeUpperBound - offsetToPreventPlayBleedingToZeroethChapter);
                return new Observable(function (observer) {
                    // wait for chapter change
                    actions.pipe(ofActionCompleted(SelectChapterById), take(1)).subscribe(function () {
                        var playParams = { numChapters: 1, queue: false, loop: loop };
                        params.time !== undefined
                            ? animationService.play(__assign({}, playParams, { startTime: adjTime }))
                            : animationService.play(__assign({}, playParams, { startChapterId: params.chapterId }));
                        observer.next(true);
                        observer.complete();
                    });
                    var chapterId = (function () {
                        if (params.chapterId) {
                            return params.chapterId;
                        }
                        return animationService.mapGlobalTimeToLocal(adjTime, 
                        // weird border boundary nonsense.  Check the comments on the definition of mapGlobalTimeToLocalTime in the Engine.
                        // if we are playing from time zero, we should start at the zeroth chapter.
                        // at time 0, the last chapter is the 'first' chapter because it's to the left of the boundary. O_o
                        { tie: 'later' }).branch.id;
                    })();
                    store.dispatch(new SelectChapterById(chapterId));
                });
            }
        },
        {
            procName: MessageEnum.TimelinePause,
            actionFn: function () {
                animationService.pause();
                return true;
            }
        },
        {
            procName: MessageEnum.TimelineSet,
            actionFn: function (params) {
                // TimelineSet requires jumping through some hoops.
                // The application is now responsible for managing chapter changes
                // The engine must only be used for scrubbing time, not chapters.
                // So, the strategy is:  Set chapter - then scrub to a time within chapter, if needed.
                // noop
                if (params.time === undefined && params.chapterId === undefined) {
                    return true;
                }
                if (params.chapterId !== undefined) {
                    store.dispatch(new SelectChapterById(params.chapterId));
                    return true;
                }
                // time should not be lower than zero
                var timeLowerBound = Math.max(0, params.time);
                // time should not be greater than upper timeline bound
                var timeUpperBound = animationService.getTimeFrame().lastTime;
                var adjTime = Math.min(timeLowerBound, timeUpperBound);
                return new Observable(function (observer) {
                    // listen for chapter changes, so we know it's safe to scrub
                    actions.pipe(ofActionCompleted(SelectChapterById), take(1)).subscribe(function () {
                        // confusing "first chapter is really the 'next' chapter paradigm"
                        // read comments at definition for Root.prototype.mapGlobalTime
                        animationService.scrub({ time: adjTime, which: adjTime === 0 ? 'second' : 'first' });
                        // safe to proceed
                        observer.next(true);
                        observer.complete();
                    });
                    var mapped = animationService.mapGlobalTimeToLocal(adjTime, adjTime === 0 ? { tie: 'later' } : {});
                    store.dispatch(new SelectChapterById(mapped.branch.id));
                });
            }
        },
        {
            procName: MessageEnum.TimelineNext,
            actionFn: function () {
                store.dispatch(new NextChapter());
                return true;
            }
        },
        {
            procName: MessageEnum.TimelinePrev,
            actionFn: function () {
                store.dispatch(new PreviousChapter());
                return true;
            }
        }
    ];
}
